import React, {
    useContext, useCallback, useState, useEffect, useRef
} from 'react';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import _ from 'lodash';
import { useDataRefresh, ViewModelServiceContext, ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { MLSortSubmissionService } from 'ml-portals-services-js';
import { MLMultiLobsDataHolderService } from 'ml-services-common-react';
import { MLLocalDateUtil, convertToSentenceCase } from 'ml-portals-util-js';
import { useTranslator } from '@jutro/locale';

import PropTypes from 'prop-types';
import metadata from './StartQuoteComponent.metadata.json5';
import styles from './StartQuoteComponent.module.scss';
import messages from './StartQuoteComponent.messages';

const StartQuoteComponent = (props) => {
    const {
        onEnterProductFlow,
        onTogglePageState
    } = props;

    const { refreshData } = useDataRefresh();
    const translator = useTranslator();
    const [quoteRequestVM, updateQuoteRequestVM] = useState({});
    const [products, setProducts] = useState([]);
    const { onValidate, isComponentValid } = useValidation('StartQuoteComponent');
    const [showErrors, setShowErrors] = useState(false);
    const [showProductSelectionError, setShowProductSelectionError] = useState(false);
    const [productSelectionErrorMessage, setProductSelectionErrorMessage] = useState('');
    const viewModelService = useContext(ViewModelServiceContext);
    const isQnB = appConfig.portalCode === 'QB';
    const inputRef = useRef(null);

    useEffect(
        () => {
            if (viewModelService !== undefined) {
                const quoteRequestViewModel = viewModelService.create(
                    {
                        effectiveDate: MLLocalDateUtil.date2LocalDate(new Date()),
                        portalCode_ML: appConfig.portalCode
                    },
                    'pc',
                    'edge.capabilities.policycommon.availability.dto.PostalCodeAvailabilityRequestDTO'
                );
                updateQuoteRequestVM(quoteRequestViewModel);
            }
        },
        [viewModelService]
    );

    const handleQuoteRequestVMChange = useCallback(
        (newModel) => {
            updateQuoteRequestVM(newModel);
            refreshData();
        },
        [refreshData]
    );

    const setStartingProduct = useCallback(
        (product) => {
            const newQuoteRequestVM = viewModelService.clone(quoteRequestVM);
            _.set(newQuoteRequestVM, 'productCode', product.productCode);
            _.set(newQuoteRequestVM, 'coveragePartType_ML', product.productType);
            handleQuoteRequestVMChange(newQuoteRequestVM);
        },
        [quoteRequestVM, viewModelService, handleQuoteRequestVMChange]
    );

    const productSelectionVisibility = () => {
        if (products.length === 0) {
            setShowProductSelectionError(true)
            setProductSelectionErrorMessage('Please select atleast one product')
        } else {
            setShowProductSelectionError(false)
            setProductSelectionErrorMessage('')
        }
    }    

    const sortProducts = (productList) => {
        const isPA = (product) => product.productCode === 'PersonalAuto';
        const isHO = (product) => product.productCode === 'HOPHomeowners';
        const isPUP = (product) => product.productCode === 'PersonalUmbrella';
        MLSortSubmissionService.sortSubmissions(productList, isPA, isHO, isPUP);
    };

    const addProduct = ({ product: selectedProduct }) => {
        // setStartingProduct(selectedProduct);
        // setProducts([selectedProduct]);

        //Comment out original functionality in case it is needed again
        if (!products.some((product) => product.productCode === selectedProduct.productCode)) {
            const productsCopy = [...products, selectedProduct];

            sortProducts(productsCopy);
            setStartingProduct(productsCopy[0]);
            setProducts(productsCopy);
        }

        setShowProductSelectionError(false)
        setProductSelectionErrorMessage('')
    };

    const removeProduct = ({ productCode }) => {
        setProducts(products.filter((product) => product.productCode !== productCode));
    };

    const setPolicyType = (policyType, coverageType) => {
        const jobToAdd = coverageType ? {
            productCode: policyType,
            coveragePartType: coverageType
        } : {
            productCode: policyType,
        };

        MLMultiLobsDataHolderService.addJob(jobToAdd);
    };

    const liveLOBs = (product) => {
        setPolicyType(product.productCode, product.productType);
    };

    const setProductsToCreate = () => {
        if (products.length > 0) {
            sortProducts(products);
            liveLOBs(products[0]);
        }
    };

    const refreshQuoteRequestVMValueEffectiveDate = useCallback(() => {
        _.set(quoteRequestVM, 'value.effectiveDate', MLLocalDateUtil.date2LocalDate(new Date()));
    }, [quoteRequestVM]);

    const productMatchers = {
        PersonalAuto: ({ productCode }) => productCode === 'PersonalAuto',

        HOPHomeowners: ({ productCode, productType }) =>
            productCode === 'HOPHomeowners' &&
            !['hoprental', 'hopcondo'].includes(productType),

        'HOPHomeowners hoprental': ({ productCode, productType }) =>
            productCode === 'HOPHomeowners' && productType === 'hoprental',

        'HOPHomeowners hopcondo': ({ productCode, productType }) =>
            productCode === 'HOPHomeowners' && productType === 'hopcondo',
        };

    const checkActiveProduct = (value) => {
        const matcher = productMatchers[value];

        return matcher ? products.some(matcher) : false;
    };

    const [stateMessages, setStateMessages] = useState([]);

    const getQuote = () => {
        productSelectionVisibility();

        if (!isComponentValid) {
            setShowErrors(true);
            
            if (isQnB) {
                inputRef.current.focus();
            }
        } else if (onEnterProductFlow && products.length > 0) {
            setProductsToCreate();
            refreshQuoteRequestVMValueEffectiveDate();
            onEnterProductFlow(quoteRequestVM.value, products[0].wizardLink, products);
        }
    };

    const getValidationMessageZipCode = (event) => {
        setStateMessages([]); // reset validation

        const {value} = event.target;

        if (value?.length < 5 || value?.length === 0) {
            setStateMessages(prev => [...prev, "Please Enter a 5-digit zipcode"]);
        } else {
            setStateMessages([]);
        }
    };    

    const overrideProps = {
        postalCodeInput: {
            ref: isQnB ? inputRef : "",
            onBlur: getValidationMessageZipCode,
            validationMessages: stateMessages,
            maxLength: 5
        },
        startQuoteTitle: {
            className: isQnB ? "vertical-spacing-50 page-header headerOne startQuoteTitle" : "vertical-spacing-50 page-header"
        },
        selectedProductsIterable: {
            data: products,
            className: isQnB ? "margin-botton-15" : ""
        },
        startQuoteContentContainer2: {
            className: isQnB ? "vertical-spacing-20 productContainerQnB" : "vertical-spacing-20",
            'aria-labelledby': isQnB ? "Product Options Group" : "",
        },
        homeProductBtn: {
            className: isQnB ? `productOptions secondaryButton ${checkActiveProduct("HOPHomeowners") ? "selectedProduct" : ""}` : "",
            content: isQnB ? convertToSentenceCase(translator(messages.homeBtn)) : translator(messages.homeBtn),
            'aria-label': isQnB ? "Home Product" : ""
        },
        autoProductBtn: {
            className: isQnB ? `productOptions secondaryButton ${checkActiveProduct("PersonalAuto") ? "selectedProduct" : ""}` : "",
            content: isQnB ? convertToSentenceCase(translator(messages.autoBtn)) : translator(messages.autoBtn),
            'aria-label': isQnB ? "Auto Product" : ""
        },
        rentersProductBtn: {
            visible: true,
            className: isQnB ? `productOptions secondaryButton ${checkActiveProduct("HOPHomeowners hoprental") ? "selectedProduct" : ""}` : "",
            content: isQnB ? convertToSentenceCase(translator(messages.renters)) : translator(messages.rentersBtn),
            'aria-label': isQnB ? "Renters Product" : ""
        },
        condoProductBtn: {
            visible: true,
            className: isQnB ? `productOptions secondaryButton ${checkActiveProduct("HOPHomeowners hopcondo") ? "selectedProduct" : ""}` : "",
            content: isQnB ? convertToSentenceCase(translator(messages.condo)) : translator(messages.condoBtn),
            'aria-label': isQnB ? "Condo Product" : ""
        },
        umbrellaProductBtn: {
            visible: false,
            className: isQnB ? "productOptions secondaryButton" : "",
            content: isQnB ? convertToSentenceCase(translator(messages.umbrellaBtn)) : translator(messages.umbrellaBtn),
            'aria-label': isQnB ? "Umbrella Product" : ""
        },
        productSelectionErrorMessage: {
            visible: isQnB ? showProductSelectionError : false,
            content: isQnB ? productSelectionErrorMessage : ''
        },
        getQuoteBtn: {
            className: isQnB ? "vertical-spacing-30 primaryButton margin-left-0" : "vertical-spacing-30",
            content: isQnB ? convertToSentenceCase(translator(messages.getQuoteBtn)) : translator(messages.getQuoteBtn)
        },
        retrieveQuoteLink: {
            "role": isQnB ? "link" : "button"
        }
    };

    const onRetrieveQuote = () => {
        onTogglePageState(_.get(quoteRequestVM, 'postalCode.value'));
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onToggleRetrieveQuote: onRetrieveQuote,
            onAddProduct: addProduct,
            onRemoveProduct: removeProduct,
            onGetQuote: getQuote
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
            overrideProps={overrideProps}
            model={quoteRequestVM}
            onModelChange={handleQuoteRequestVMChange}
            onValidationChange={onValidate}
            showErrors={showErrors}
        />
    );
};

StartQuoteComponent.propTypes = {
    onEnterProductFlow: PropTypes.func.isRequired,
    onTogglePageState: PropTypes.func.isRequired
};

export default StartQuoteComponent;
